import { isValidElement, type ReactNode } from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { cn } from '../lib/cn';

/**
 * Genie's answer, rendered.
 *
 * ── Why this exists ───────────────────────────────────────────────────────
 *
 * Answers used to be printed with `whitespace-pre-wrap`, so the system prompt
 * had to forbid the model any formatting at all — "no asterisks, no hashes, no
 * bullet characters" — because anything it wrote would appear literally. That
 * ban is what produced answers like:
 *
 *   March: £430.10 total. TOBACCO £110.23; DEFAULT £95.17; WINES SPIRITS
 *   BEERS £89.75; HOUSEHOLD, CLEANING & PAPER £74.99; SOFT DRINKS £21.32…
 *
 * which is a table with semicolons for column rules. Six months by twelve
 * departments is a grid, and a grid set as prose cannot be read down a column,
 * cannot be scanned for the largest figure, and cannot be compared month to
 * month — which was the entire question.
 *
 * ── Why it is not a generic markdown theme ────────────────────────────────
 *
 * A table here is rendered in the console's own table language: hairline rules,
 * eyebrow headers, tight rows. Cells that hold figures are set in the mono face
 * and right-aligned automatically, by matching the cell text rather than by
 * trusting the model to have remembered GFM's `---:` — the money in a Genie
 * answer should line up exactly as it does everywhere else in QuickStock, and
 * whether it does must not depend on what the model typed.
 *
 * Raw HTML is not enabled, which matters because this content is
 * model-generated: `react-markdown` drops embedded HTML unless `rehype-raw` is
 * added, so there is no path from an answer to injected markup.
 */

/** Money, counts and percentages — anything that should be set as a figure. */
const FIGURE = /^[-+]?[£$€]?-?[\d,]+(?:\.\d+)?%?$/;

function isFigure(text: string): boolean {
  const trimmed = text.trim();
  if (trimmed === '') return false;
  return FIGURE.test(trimmed);
}

/** Flattens a cell's rendered children back to text, to classify it. */
function textOf(node: ReactNode): string {
  if (node === null || node === undefined || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(textOf).join('');
  if (isValidElement(node)) {
    return textOf((node.props as { children?: ReactNode }).children);
  }
  return '';
}

export function AnswerBody({ content }: { content: string }): React.JSX.Element {
  return (
    <div className="text-sm leading-relaxed">
      <Markdown
        remarkPlugins={[remarkGfm]}
        components={{
          p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,

          strong: ({ children }) => <strong className="font-semibold">{children}</strong>,

          ul: ({ children }) => (
            <ul className="mb-2 ml-4 list-disc space-y-0.5 last:mb-0 marker:text-ink-faint">
              {children}
            </ul>
          ),
          ol: ({ children }) => (
            <ol className="mb-2 ml-4 list-decimal space-y-0.5 last:mb-0 marker:text-ink-faint">
              {children}
            </ol>
          ),
          li: ({ children }) => <li className="pl-0.5">{children}</li>,

          /* Headings are unlikely in a two-paragraph answer, but a long
             breakdown may reach for one. Kept close to body size — a 24px
             heading inside a 13px console is a different product's typography. */
          h1: ({ children }) => <Heading>{children}</Heading>,
          h2: ({ children }) => <Heading>{children}</Heading>,
          h3: ({ children }) => <Heading>{children}</Heading>,
          h4: ({ children }) => <Heading>{children}</Heading>,

          hr: () => <hr className="my-3 border-rule" />,

          blockquote: ({ children }) => (
            <blockquote className="mb-2 border-l-2 border-rule pl-3 text-ink-muted last:mb-0">
              {children}
            </blockquote>
          ),

          code: ({ children, className }) =>
            /* `react-markdown` routes both inline code and fenced blocks here;
               only a fenced block carries a language class, and only it is
               wrapped in a <pre>. */
            className ? (
              <code className={cn('font-mono text-2xs', className)}>{children}</code>
            ) : (
              <code className="rounded border border-rule bg-sunken px-1 py-0.5 font-mono text-2xs">
                {children}
              </code>
            ),
          pre: ({ children }) => (
            <pre className="mb-2 overflow-x-auto rounded border border-rule bg-sunken p-2 font-mono text-2xs last:mb-0">
              {children}
            </pre>
          ),

          /* A wide table scrolls inside its own box rather than pushing the
             thread sideways or being squeezed to fit. Six months across the top
             is six columns and a sideways scroll — cramming it into the measure
             would mean wrapping every heading, and splitting it in two would
             mean the reader cannot compare March with August at all.

             `w-max min-w-full` is what allows that: `w-full` alone makes the
             table shrink to the container and wrap its cells instead of
             overflowing, so the scroll never engages. */
          table: ({ children }) => (
            <div className="qs-scroll mb-2 overflow-x-auto last:mb-0">
              <table className="w-max min-w-full border-collapse">{children}</table>
            </div>
          ),
          thead: ({ children }) => <thead>{children}</thead>,
          tbody: ({ children }) => <tbody>{children}</tbody>,
          tr: ({ children }) => <tr className="border-b border-rule last:border-0">{children}</tr>,

          th: ({ children, style }) => (
            <th
              className={cn(
                'qs-eyebrow py-1.5 pr-3 align-bottom whitespace-nowrap last:pr-0',
                style?.textAlign === 'right' ? 'text-right' : 'text-left',
              )}
            >
              {children}
            </th>
          ),

          td: ({ children, style }) => {
            // Classified from the cell's own text, not from the column's
            // declared alignment: the model does not reliably write `---:`, and
            // a money column that fails to line up is worse than one that does
            // not say it is a money column.
            const figure = isFigure(textOf(children));

            return (
              <td
                className={cn(
                  'py-1.5 pr-3 align-top text-xs last:pr-0',
                  figure ? 'qs-figure text-right' : 'text-left',
                  !figure && style?.textAlign === 'right' && 'text-right',
                )}
              >
                {children}
              </td>
            );
          },

          a: ({ children, href }) => (
            <a href={href} className="text-accent underline underline-offset-2">
              {children}
            </a>
          ),
        }}
      >
        {content}
      </Markdown>
    </div>
  );
}

function Heading({ children }: { children: ReactNode }): React.JSX.Element {
  return <p className="mt-3 mb-1 text-xs font-semibold first:mt-0">{children}</p>;
}
